import { FC, useCallback, useContext, useEffect, useRef, useState } from "react";
import { fabric } from 'fabric';

import { FabricContext, TeamContext, UserContext, SocketContext, CardContext, BoardContext } from "../../../context";

import { SpinWheelNotification, WheelHandle } from "../../atoms";
import { DilemmaOwnerCoinCelebration } from "../../organisms";
import { WheelOfFortuneHelper } from "../../molecules";
import { TIMES_TO_SPIN } from "./WheelOfFortune.constants";
import { IGame } from "../../../interfaces";
import { usePageVisibility, useSound } from "../../../hooks";
import { calculateAngleFromPrize } from "./helpers/index";
import { Phase } from "../../../util/enums";

import roulette from "../../../assets/images/spinWheel.svg";
import pin from "../../../assets/images/spinWheelPin.svg";

const WheelOfFortuneMolecule: FC<{}> = () => {
    const { startSpinSocketEvent, spinNumberSocketOn, spinNumberData, finishActionCardSocketEvent, } = useContext(SocketContext);
    const { canvas, initCanvas } = useContext(FabricContext);
    const { userData } = useContext(UserContext);
    const { setGameState, game } = useContext(TeamContext);
    const { setCurrentlyHoveredStrengthCard } = useContext(CardContext);
    const { isWheelSpinningDone, setIsWheelSpinningDone } = useContext(BoardContext);

    const [showConfeti, setShowConfeti] = useState(false);
    const [disabled, setDisabled] = useState(false);

    const windowLocationHref = window.location.href;
    const isSpectator : boolean = windowLocationHref.includes("spectator");
    const choosenSolutionUserStyle: string = (game?.gameInfo.currentSpinOwner === userData?.email || isSpectator) ? "" : "visibility_hidden";
    
    const isTheUserSpinOwner = (userData?.email === game?.gameInfo.currentSpinOwner || isSpectator);
    const [isVisible] = usePageVisibility();
    const canvasRef = useRef(null);
    const currentTokenValue = useRef(0);
    const {playSound} = useSound();

    let timeForTheWheelToSpin = 2500;

    const formatElement = useCallback((objects: fabric.Object[], options: any, onComplete: (element: fabric.Object | fabric.Group) => void) => {
        if (!canvas) return;

        canvas.hoverCursor = "default";
        const canvasCenter = canvas.getCenter();
        const element = fabric.util.groupSVGElements(objects, options);
        element.set({
            left: canvasCenter.left,
            top: canvasCenter.top,
            originX: 'center',
            originY: 'center',
            scaleX: 0.8,
            scaleY: 0.8,
            lockMovementY: true,
            lockScalingX: true,
            lockScalingY: true,
            lockRotation: true,
            hasControls: element.hasBorders = false,
            objectCaching: false
        });
        onComplete(element);
    }, [canvas])

    const createPin = useCallback(() => {
        if (!canvas) return;

        const pinFullPath = process.env.REACT_APP_FRONTEND_URL!
            .toString()
            .concat(pin);
        const optionsOverride = { height: 400, id: "pin" };

        fabric.loadSVGFromURL(pinFullPath, (objects, options) => formatElement(objects, { ...options, ...optionsOverride }, (element) => {
            canvas.add(element);
            canvas.bringForward(element, true);
            canvas.renderAll();
        }));
    }, [canvas, formatElement])
    
    const createWheel = useCallback(() => {
        if (!canvas) return;

        const rouletteFullPath = process.env.REACT_APP_FRONTEND_URL!.toString().concat(roulette);

        fabric.loadSVGFromURL(rouletteFullPath, (objects, options) => formatElement(objects, { ...options, }, (element) => {
            canvas.add(element);
            canvas.setActiveObject(element);
            canvas.renderAll();

            createPin();
        }));
    }, [canvas, createPin, formatElement]);

    const checkBeforeCreateTheWheel = useCallback(() => {
        if (canvas && game?.phase === Phase.RENDER_SPIN_WHEEL)
            createWheel();
    }, [canvas, createWheel, game?.phase]);

    useEffect(() => {
        checkBeforeCreateTheWheel();
    }, [checkBeforeCreateTheWheel]);
        
    const setCanvasInitialValues = useCallback(() => {
        if (!canvas && !isWheelSpinningDone) {
            const localCanvas = new fabric.Canvas("wheel_canvas", {
                preserveObjectStacking: true,
                width: 750,
                height: 750,
                selection: false
            });
            initCanvas(localCanvas);
        }
    }, [canvas, initCanvas, isWheelSpinningDone]);

    useEffect(() => {
        setCanvasInitialValues()
    }, [setCanvasInitialValues]);

    useEffect(() => {
        setCurrentlyHoveredStrengthCard(undefined)
    }, [setCurrentlyHoveredStrengthCard]);

    useEffect(() => {
        spinNumberSocketOn();
    });

    const dismantleTheSpinWheel = useCallback((gameObject: IGame) => {
        canvas?.getObjects().forEach((obj: any) => {
            if (obj.id !== "coin")
                canvas.remove(obj)
        })

        canvas?.discardActiveObject().renderAll();

        const timeout = setTimeout(() => {
            setIsWheelSpinningDone(false);
            setGameState(gameObject);
            finishActionCardSocketEvent();
        }, 4500);

        return () => clearTimeout(timeout);
    }, [canvas, finishActionCardSocketEvent, setGameState, setIsWheelSpinningDone]);

    const spinTheSpinWheel = useCallback((gameDataWithCoinToken: any) => {
        const angle = calculateAngleFromPrize(gameDataWithCoinToken.token) + 360 * TIMES_TO_SPIN;

        const object = canvas?.getActiveObject();

        object?.animate({ angle }, {
            onChange: canvas?.renderAll.bind(canvas),
            duration: timeForTheWheelToSpin,
            easing: fabric.util.ease.easeOutElastic,
            onComplete: () => {
                setIsWheelSpinningDone(true);
                setTimeout(() => {
                    dismantleTheSpinWheel(gameDataWithCoinToken.state);
                }, 100)
            }
        });
    }, [canvas, dismantleTheSpinWheel, setIsWheelSpinningDone, timeForTheWheelToSpin]);

    const triggerEffects = useCallback(() => {
        if (isVisible && spinNumberData) {
            currentTokenValue.current = spinNumberData.token;
            spinTheSpinWheel(spinNumberData);
            playSound("/sounds/Spin2.mp3");
            return;
        }
        // if I add "isVisible", then the WheelOfFortune will be waiting for me to back to the unactive tab, and just then will continuoue spinning, while all the other users moved on.
        // if I add "spinTheSpinWheel", then the function "spinTheSpinWheel" has to be with useCallback and even then spins endlessly
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [spinNumberData, playSound]);

    useEffect(() => {
        triggerEffects();
    }, [triggerEffects]);        

    const spinWheel = () => {
        setDisabled(true);
        startSpinSocketEvent(game?.gameInfo.currentSpinOwner);
    }

    const checkShowConfetti = useCallback(() => {
        if (showConfeti) {
            const confTimeout = setTimeout(() => {
                setShowConfeti(false)
            }, 5000)

            return () => clearTimeout(confTimeout)
        }
    }, [showConfeti])

    useEffect(() => {
        checkShowConfetti();
    }, [checkShowConfetti]);

    const celebration = () => {
        return (
            <div style={{ position: "absolute", top: 0, left: 0, height: '100vh', width: '100vw' }}>
                <DilemmaOwnerCoinCelebration currentTokenValue={currentTokenValue.current} />
            </div>
        );
    }

    return (
        <div className="wheel_wrapper" >
            
            <WheelOfFortuneHelper spinNumberData={spinNumberData} />

            
            <div className="notification_spin_wheel center_horizontally_and_vertically">
                {!isWheelSpinningDone && <SpinWheelNotification />}
                {isWheelSpinningDone && celebration()}
            </div>
            
            <div style={{width: "100vw", height: "1px"}}></div> 
            
            <div className="wheel_canvas_wrapper" >
                <div className="animation_ripple animation_ripple_delay_6 noClick visibility_hidden" >
                    <canvas id="wheel_canvas" ref={canvasRef} className="spin_wheel_canvas" />
                </div>
                <div className="wheel_button">
                    {!isWheelSpinningDone && isTheUserSpinOwner &&
                        <WheelHandle
                            clickHandler={() => spinWheel()}
                            disabled={disabled}
                        />
                    }
                    <div className={`${choosenSolutionUserStyle}`} style={{ zIndex: 99 }}>
                    </div>
                </div>
            </div>

            
            
        </div>
    );
}

export default WheelOfFortuneMolecule;